import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useIsMobile from './useIsMobile';
import Icon from './Icon';
import useMobileTableLabels from './useMobileTableLabels';

// Bottom navigation for the phone. Four or five destinations per position,
// chosen by what that position actually does all day; "More" opens the
// full sidebar drawer so nothing is unreachable. Desktop renders nothing.
//
// Destinations are derived from the same flags the Sidebar uses, so a
// position never gets a tab it cannot open.
function destinationsFor(auth) {
  const { isDriver, isSignTechnician, positionName, isSales, isSalesAdmin, isDispatcher, effectiveTeamNames } = auth;
  const isTagger = positionName === 'Tagger';
  const driverish = isDriver || (effectiveTeamNames || []).includes('Driver Operations');
  if (isSignTechnician) {
    return [
      { to: '/sign-route', label: 'Route', icon: 'route' },
      { to: '/workflow', label: 'Tasks', icon: 'tasks' },
      { to: '/timeclock', label: 'Clock', icon: 'clock' },
    ];
  }
  if (isTagger) {
    return [
      { to: '/tagging-route', label: 'Route', icon: 'route' },
      { to: '/workflow', label: 'Tasks', icon: 'tasks' },
    ];
  }
  if (driverish) {
    return [
      { to: '/', label: 'Home', icon: 'home', end: true },
      { to: '/dispatch', label: 'Queue', icon: 'queue' },
      { to: '/driver-chat', label: 'Chat', icon: 'chat' },
      { to: '/leaderboard', label: 'Board', icon: 'board' },
    ];
  }
  // Sales (Matt, Sep 9): reps asked for Dispatch on the bar. Time Clock
  // moves behind More — clocked in once a day, calls all day.
  if (isSales || isSalesAdmin) {
    return [
      { to: '/', label: 'Home', icon: 'home', end: true },
      { to: '/dispatch', label: 'Dispatch', icon: 'queue' },
      { to: '/workflow', label: 'Tasks', icon: 'tasks' },
      { to: '/sales/requests', label: 'Submit', icon: 'inbox' },
    ];
  }
  if (isDispatcher) {
    return [
      { to: '/', label: 'Home', icon: 'home', end: true },
      { to: '/dispatch', label: 'Dispatch', icon: 'queue' },
      { to: '/workflow', label: 'Tasks', icon: 'tasks' },
      { to: '/timeclock', label: 'Clock', icon: 'clock' },
    ];
  }
  // Admins, managers, super admin on a phone
  return [
    { to: '/', label: 'Home', icon: 'home', end: true },
    { to: '/dispatch', label: 'Dispatch', icon: 'queue' },
    { to: '/workflow', label: 'Tasks', icon: 'tasks' },
    { to: '/sales/requests', label: 'Submit', icon: 'inbox' },
  ];
}

export default function MobileTabBar({ taskCount = 0 }) {
  const auth = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const show = isMobile && !!auth.user;
  // Phone-wide table → card labelling rides along with the tab bar: it is
  // mounted once for every logged-in phone screen, which is exactly where
  // tables need to become cards.
  useMobileTableLabels(show);

  useEffect(() => {
    if (!show) return undefined;
    document.body.classList.add('has-tabbar');
    return () => document.body.classList.remove('has-tabbar');
  }, [show]);

  if (!show) return null;
  const items = destinationsFor(auth);
  const isActive = it => (it.end ? location.pathname === it.to : location.pathname.startsWith(it.to));

  return (
    <nav className="m-tabbar" aria-label="Primary">
      {items.map(it => (
        <NavLink key={it.to} to={it.to} end={it.end} className={`m-tab ${isActive(it) ? 'active' : ''}`}>
          <Icon name={it.icon} size={24} />
          <span>{it.label}</span>
          {it.to === '/workflow' && taskCount > 0 && <div className="m-tab-badge">{taskCount > 99 ? '99+' : taskCount}</div>}
        </NavLink>
      ))}
      <button type="button" className="m-tab" onClick={() => window.dispatchEvent(new CustomEvent('apollo:toggle-menu'))}>
        <Icon name="more" size={24} />
        <span>More</span>
      </button>
    </nav>
  );
}
